import { Helmet } from 'react-helmet-async';

// Primary, canonical domain. The live site is served from www.newcreative.co.za
// (apex newcreative.co.za 308-redirects here). Everything the crawlers read must
// point here — canonicals, OG images, sitemap and schema.
export const SITE_URL = 'https://www.newcreative.co.za';
const DEFAULT_IMAGE = `${SITE_URL}/brand/hero.jpg`;

/**
 * Per-page SEO / GEO / AEO head tags.
 *
 * The site-wide entity graph (Organization / LocalBusiness / WebSite / Services)
 * lives statically in public/index.html so AI crawlers that don't execute JS still
 * read it. This component adds the per-page layer: title, description, canonical,
 * social cards and an optional BreadcrumbList.
 */
export const SEO = ({
  title,
  description,
  path = '',
  image,
  noIndex = false,
  breadcrumbs,
}) => {
  const fullTitle = title
    ? `${title} | New Creative Industries`
    : 'New Creative Industries | Premium Event Production Pretoria';
  const fullUrl = `${SITE_URL}${path}`;
  const ogImage = image ? `${SITE_URL}${image}` : DEFAULT_IMAGE;

  const breadcrumbSchema =
    breadcrumbs && breadcrumbs.length
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((crumb, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: crumb.name,
            item: `${SITE_URL}${crumb.path}`,
          })),
        }
      : null;

  return (
    <Helmet>
      <html lang="en-ZA" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />
      <meta
        name="robots"
        content={
          noIndex
            ? 'noindex,nofollow'
            : 'index,follow,max-image-preview:large,max-snippet:-1'
        }
      />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="New Creative Industries" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:locale" content="en_ZA" />

      {/* Twitter / X Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {breadcrumbSchema ? (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      ) : null}
    </Helmet>
  );
};
